import { useMemo } from 'react';
import { useList } from '@refinedev/core';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface TelegramBotOption {
  id: number;
  name: string;
  username: string | null;
  isActive: boolean;
}

interface BotSelectProps {
  /** Текущий botId. null — режим «По умолчанию (первый активный)». */
  value: number | null;
  /** Колбэк для нового значения. null — сброс на default. */
  onChange: (value: number | null) => void;
  disabled?: boolean;
  /** Сообщение в trigger когда botId=null. */
  placeholder?: string;
  /** Скрыть пункт «По умолчанию» (если бот должен быть обязателен). */
  required?: boolean;
}

/**
 * Универсальный селектор Telegram-бота для admin-форм.
 *
 * Заменяет неявный fallback «использовать isPrimary» — теперь админ явно
 * выбирает, через какого бота уйдёт рассылка/приглашение/ссылка. Если бот
 * не выбран и поле не required, на бэкенде сработает fallback на первого
 * активного (по createdAt).
 *
 * Используется в:
 *  - broadcasts/form (рассылки → botId)
 *  - user-invitations/dialogs (приглашения → botId)
 *  - triggers actions (Telegram-действия → botId)
 */
export function BotSelect({
  value,
  onChange,
  disabled,
  placeholder = 'По умолчанию (первый активный)',
  required = false,
}: BotSelectProps) {
  const botsResult = useList<TelegramBotOption>({
    resource: 'bot-tokens',
    pagination: { pageSize: 200, mode: 'server' },
    queryOptions: { staleTime: 60_000 },
  });

  const options = useMemo<TelegramBotOption[]>(
    () =>
      (botsResult.query.data?.data ?? []).filter((b) => b.isActive),
    [botsResult.query.data],
  );

  const isLoading = botsResult.query.isLoading;

  // value=null → передаём '' триггеру, чтобы placeholder отрисовался.
  const stringValue = value === null ? '' : String(value);

  return (
    <Select
      value={stringValue}
      onValueChange={(v) => onChange(v === '__default__' ? null : Number(v))}
      disabled={disabled || isLoading}
    >
      <SelectTrigger>
        <SelectValue placeholder={isLoading ? 'Загрузка ботов…' : placeholder} />
      </SelectTrigger>
      <SelectContent>
        {!required && (
          <SelectItem value="__default__">{placeholder}</SelectItem>
        )}
        {options.map((bot) => (
          <SelectItem key={bot.id} value={String(bot.id)}>
            {bot.username ? `${bot.name} (@${bot.username})` : bot.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
